import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, Row, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import MainCreateBank from "../CreateBank/MainCreateBank";
import UpdateBankFooter from "./UpdateProductFooter";
import { FC, useCallback, useEffect, useRef } from "react";
import { useGetBankById, useGetLogoBank, useUpdateBankFactory } from "hooks/useFactory";
import { CreateBankInput, FileDocumentDto } from "services/interface";
import { useGetMasterBankType } from "hooks/useMasterData";
import { filterDocumentsToActiveSlots, getDocumentSlotKey, getSlotsForBankType } from "../CreateBank/bankDocumentConfig";

const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 12 },
    },
  };
interface UpdateBankProps {
    id: string,
}
const UpdateBank: FC<UpdateBankProps>= ({ id }) => {
  const navigate = useNavigate();
  const { data: dataLogo } = useGetLogoBank();
  const { data: dataBankType } = useGetMasterBankType();
  const { data: dataBank, refetch:refetchBank } = useGetBankById(id);
  const { mutate: UpdateBank, isPending } = useUpdateBankFactory();

  const [form] = Form.useForm();
  const documentMapRef = useRef<Record<string, FileDocumentDto>>({});

  const handleDocumentChange = useCallback(
    (document_type: string, dto: FileDocumentDto | null) => {
      if (document_type === "__reset__") {
        documentMapRef.current = {};
        return;
      }
      if (dto === null) {
        delete documentMapRef.current[document_type];
      } else {
        documentMapRef.current[document_type] = dto;
      }
    },
    []
  );
  //   const queryClient = useQueryClient();
  useEffect(() => {
    if (dataBank) {
      const data = dataBank?.data[0];
      form.setFieldsValue({
        bank_key: data.bank_key,
        account_name: data.account_name,
        account_no : data.account_no,
        bank_type_id : data.bank_type_id,
      });
      documentMapRef.current = {};
      data.document_list?.forEach((doc) => {
        const slotKey = getDocumentSlotKey(doc);
        if (!slotKey) return;
        documentMapRef.current[slotKey] = {
          file_id: doc.file_id,
          document_type: slotKey,
          name: slotKey,
          description: doc.description,
        };
      });
    }
  }, [dataBank]);

  const onFinish = (values: any) => {
    const currentDesc =
      dataBankType?.data?.find((t) => String(t.id) === String(values.bank_type_id))
        ?.description ?? "";
    const requiredSlots = getSlotsForBankType(currentDesc).filter((s) => s.required);
    const missingSlots = requiredSlots.filter(
      (s) => !documentMapRef.current[s.document_type]
    );
    if (missingSlots.length > 0) {
      message.warning(
        `กรุณาอัพโหลดเอกสาร: ${missingSlots.map((s) => s.label).join(", ")}`
      );
      return;
    }
    const { bank_key } = values;     
    const bank_name = (dataLogo?.find((item) => item.bank_key === bank_key))?.name;
    const dataUpdate: CreateBankInput = {
      id: id,
      bank_name: bank_name ?? '',
      ...values,
      document_list: filterDocumentsToActiveSlots(
        Object.values(documentMapRef.current),
        currentDesc
      ),
    }
    UpdateBank(dataUpdate, {
      onSuccess: (data) => {
        if (data.status === "success") {
          message.success("แก้ไขบัญชีสำเร็จ");
          navigate('/manage-bank')
        }else {
          message.error(`แก้ไขบัญชีไม่สำเร็จ ${data.error_message?.[0]}`);
        }
      },
      onError: () => {
        message.error("แก้ไขบัญชีไม่สำเร็จ");
      },
    });
  };

  return (
    <Form form={form} {...formItemLayout} onFinish={onFinish}>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/manage-bank");
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>แก้ไขบัญชี</Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>
              <MainCreateBank
                form={form}
                dataLogo={dataLogo}
                onDocumentChange={handleDocumentChange}
                initialDocuments={dataBank?.data[0]?.document_list}
              />
            </WrapperAll>
          </Card>
        </Col>
      </Row>
      <UpdateBankFooter loading={isPending} />
    </Form>
  );
};

export default UpdateBank;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
